import { useState } from "react";
import { VerticalLayout, GridLayout } from "./layout";
import { TextAreaForm } from "../atoms/input";
import { StandardButton } from "../atoms/buttons";
import { icon } from "../functions/icons";
import { postAPI, ip } from "../api/api";
import ColorPicker from "../atoms/colorpicker";

const IMAGE_STYLE_TEMPLATES = [
    {
        id: 1,
        name: "Flowchart",
        image: "https://i.imgur.com/58GgMag.png"
    },
    {
        id: 2,
        name: "Infographic",
        image: "https://i.imgur.com/JgFS0gn.png"
    },
    {
        id: 3,
        name: "Custom",
        image: "https://img.freepik.com/premium-vector/custom-hand-drawn-lettering-phrase-vector_956226-72.jpg"
    },
];

const DEFAULT_RESULT_COUNT = 2;

export function AIImageGenerator({ onImageSelected }) {
    const [prompt, setPrompt] = useState("");
    const [selectedStyle, setSelectedStyle] = useState(null);
    const [mainColor, setMainColor] = useState("#3B82F6");
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedImages, setGeneratedImages] = useState([]);
    const [error, setError] = useState("");

    async function handleGenerate() {
        if (!prompt.trim() || !selectedStyle) return;

        setIsGenerating(true);
        setError("");
        setGeneratedImages([]);

        try {
            const result = await postAPI("api/media/generate-image", {
                prompt: prompt,
                style: selectedStyle.id,
                color: mainColor,
                count: DEFAULT_RESULT_COUNT
            });

            if (result?.success && result?.data?.images) {
                const imagesWithFullUrl = result.data.images.map(img => ({
                    ...img,
                    url: `${ip}/api/media/${img.url}`
                }));
                setGeneratedImages(imagesWithFullUrl);
            } else {
                setError(result?.message || "Er ging iets mis bij het genereren.");
            }
        } catch (err) {
            setError("Er ging iets mis bij het genereren. Probeer opnieuw.");
        } finally {
            setIsGenerating(false);
        }
    }

    function handleSelectImage(image) {
        if (onImageSelected) {
            onImageSelected(image);
        }
    }

    function handleRegenerate() {
        handleGenerate();
    }

    if (isGenerating) {
        return <GeneratingState />;
    }

    if (generatedImages.length > 0) {
        return (
            <ImageResultsView
                images={generatedImages}
                onSelectImage={handleSelectImage}
                onRegenerate={handleRegenerate}
            />
        );
    }

    return (
        <ImageGeneratorForm
            prompt={prompt}
            setPrompt={setPrompt}
            selectedStyle={selectedStyle}
            setSelectedStyle={setSelectedStyle}
            mainColor={mainColor}
            setMainColor={setMainColor}
            onGenerate={handleGenerate}
            error={error}
        />
    );
}

function GeneratingState() {
    return (
        <VerticalLayout style={{ width: "420px", padding: "3rem 0", gap: "1.5rem", alignItems: "center" }}>
            <div
                style={{
                    width: "72px",
                    height: "72px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-dark) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    animation: "aiPulse 1.5s ease-in-out infinite"
                }}
            >
                <span style={{ color: "white" }}>{icon("sparkles", 28)}</span>
            </div>
            <VerticalLayout style={{ gap: "0.5rem", alignItems: "center" }}>
                <span className="standard-text bold" style={{ color: "var(--standard-text-color)" }}>
                    Afbeelding genereren
                </span>
                <span className="small-text" style={{ color: "var(--grey-600)" }}>
                    Even geduld, AI is bezig...
                </span>
            </VerticalLayout>
            <style>{`
                @keyframes aiPulse {
                    0%, 100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.05); opacity: 0.8; }
                }
            `}</style>
        </VerticalLayout>
    );
}

function ImageResultsView({ images, onSelectImage, onRegenerate }) {
    return (
        <VerticalLayout style={{ width: "420px", gap: "1rem" }}>
            <span className="small-text bold" style={{ color: "var(--standard-text-color)" }}>
                Kies een afbeelding
            </span>
            <GridLayout columnsTemplate="repeat(2, 1fr)" style={{ gap: "1rem" }}>
                {images.map((image, index) => (
                    <ImageResultItem
                        key={index}
                        image={image}
                        onSelect={() => onSelectImage(image)}
                    />
                ))}
            </GridLayout>
            <StandardButton
                title="Opnieuw genereren"
                onClick={onRegenerate}
                icon="refresh"
                style={{ marginLeft: "auto" }}
            />
        </VerticalLayout>
    );
}

function ImageResultItem({ image, onSelect }) {
    return (
        <div
            onClick={onSelect}
            className="ai-image-result-item"
            style={{
                position: "relative",
                aspectRatio: "1",
                borderRadius: "8px",
                overflow: "hidden",
                cursor: "pointer",
                border: "2px solid var(--grey-200)",
                transition: "all 0.2s ease"
            }}
        >
            <img
                src={image.url}
                alt="Generated"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <div
                className="ai-image-overlay"
                style={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(transparent 50%, rgba(0,0,0,0.6))",
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    padding: "0.75rem",
                    opacity: 0,
                    transition: "opacity 0.2s ease"
                }}
            >
                <span className="xsmall-text bold" style={{ color: "white" }}>
                    Selecteren
                </span>
            </div>
            <style>{`
                .ai-image-result-item:hover {
                    border-color: var(--primary-color) !important;
                    transform: scale(1.02);
                }
                .ai-image-result-item:hover .ai-image-overlay {
                    opacity: 1 !important;
                }
            `}</style>
        </div>
    );
}

function ImageGeneratorForm({
    prompt,
    setPrompt,
    selectedStyle,
    setSelectedStyle,
    mainColor,
    setMainColor,
    onGenerate,
    error
}) {
    const canGenerate = prompt.trim() && selectedStyle;

    return (
        <VerticalLayout style={{ width: "420px", gap: "1.25rem" }}>
            <StyleSelector
                selectedStyle={selectedStyle}
                onSelectStyle={setSelectedStyle}
            />

            <ColorPickerSection
                mainColor={mainColor}
                setMainColor={setMainColor}
            />

            <TextAreaForm
                title="Beschrijving"
                placeholder="Beschrijf de afbeelding die je wilt genereren..."
                value={prompt}
                typeInput={setPrompt}
                rows={3}
            />

            {error && (
                <div style={{
                    padding: "0.75rem",
                    background: "var(--error-50, #FEF2F2)",
                    borderRadius: "8px",
                    border: "1px solid var(--error-200, #FECACA)"
                }}>
                    <span className="small-text" style={{ color: "var(--error-color, #DC2626)" }}>
                        {error}
                    </span>
                </div>
            )}

            <StandardButton
                title="Genereer afbeelding"
                onClick={onGenerate}
                disabled={!canGenerate}
                icon="sparkles"
            />
        </VerticalLayout>
    );
}

function StyleSelector({ selectedStyle, onSelectStyle }) {
    return (
        <VerticalLayout style={{ gap: "0.5rem" }}>
            <span className="small-text bold" style={{ color: "var(--standard-text-color)" }}>
                Kies een stijl
            </span>
            <GridLayout columnsTemplate="repeat(3, 1fr)" gap="1rem">
                {IMAGE_STYLE_TEMPLATES.map((style) => (
                    <StyleOption
                        key={style.id}
                        styleData={style}
                        isSelected={selectedStyle?.id === style.id}
                        onSelect={() => onSelectStyle(style)}
                    />
                ))}
            </GridLayout>
        </VerticalLayout>
    );
}

function StyleOption({ styleData, isSelected, onSelect }) {
    return (
        <div
            onClick={onSelect}
            style={{
                position: "relative",
                borderRadius: "8px",
                overflow: "hidden",
                cursor: "pointer",
                height: "80px",
                width: "100%",
                border: isSelected ? "3px solid var(--primary-700)" : "2px solid var(--grey-200)",
                transition: "all 0.15s ease"
            }}
        >
            <img
                src={styleData.image}
                alt={styleData.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <div
                style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: "0.5rem 0.25rem",
                    background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                    textAlign: "center"
                }}
            >
                <span className="xsmall-text bold" style={{ color: "white" }}>
                    {styleData.name}
                </span>
            </div>
            {isSelected && (
                <div
                    style={{
                        position: "absolute",
                        top: "6px",
                        right: "6px",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "var(--primary-color)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white"
                    }}
                >
                    {icon("check", 14)}
                </div>
            )}
        </div>
    );
}

function ColorPickerSection({ mainColor, setMainColor }) {
    return (
        <VerticalLayout style={{ gap: "0.5rem" }}>
            <span className="small-text bold" style={{ color: "var(--standard-text-color)" }}>
                Hoofdkleur
            </span>
            <ColorPicker colorValue={mainColor} setColor={setMainColor} />
        </VerticalLayout>
    );
}
